import React, { useState, useEffect, useRef } from 'react';
import { SearchableDropdown, Button, SideBarModal } from '@/components';
import { Pitstop } from '@/services/api/pitstops';
import { showErrorToast } from '@/components/toast/globalToast';
import { BookingScheduleTimeWindow } from '@/services/api/settings';

interface CreateScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ScheduleFormData) => Promise<void>;
  isLoading?: boolean;
  pitstops: Pitstop[];
}

export interface ScheduleFormData {
  hubID: number;
  activeDays: number[];
  timeWindows: BookingScheduleTimeWindow[];
  isActive: boolean;
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      options.push({ label: timeStr, value: timeStr });
    }
  }
  options.push({ label: '23:59', value: '23:59' });
  return options;
};
const TIME_OPTIONS = generateTimeOptions();

export const CreateScheduleModal: React.FC<CreateScheduleModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  pitstops = [],
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const defaultForm = (): ScheduleFormData => ({
    hubID: 0,
    activeDays: [],
    timeWindows: [{ startTime: '', endTime: '' }],
    isActive: false,
  });

  const [formData, setFormData] = useState<ScheduleFormData>(defaultForm());

  useEffect(() => {
    if (isOpen) {
      setFormData(defaultForm());
    }
  }, [isOpen]);

  const hubOptions = pitstops.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const handleDayToggle = (dayValue: number) => {
    setFormData((prev) => {
      const isSelected = prev.activeDays.includes(dayValue);
      return {
        ...prev,
        activeDays: isSelected
          ? prev.activeDays.filter((d) => d !== dayValue)
          : [...prev.activeDays, dayValue],
      };
    });
  };

  const handleTimeWindowChange = (index: number, field: 'startTime' | 'endTime', value: string) => {
    setFormData((prev) => {
      const newWindows = [...prev.timeWindows];
      newWindows[index] = { ...newWindows[index], [field]: value };
      return { ...prev, timeWindows: newWindows };
    });
  };

  const handleAddTimeWindow = () => {
    setFormData((prev) => ({
      ...prev,
      timeWindows: [...prev.timeWindows, { startTime: '', endTime: '' }],
    }));
  };

  const handleRemoveTimeWindow = (index: number) => {
    setFormData((prev) => {
      const newWindows = prev.timeWindows.filter((_, i) => i !== index);
      return { ...prev, timeWindows: newWindows };
    });
  };

  const isFormValid =
    formData.hubID !== 0 &&
    formData.activeDays.length > 0 &&
    formData.timeWindows.length > 0 &&
    formData.timeWindows.every((tw) => tw.startTime && tw.endTime);

  const handleSave = async () => {
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error(error);
      const msg =
        error instanceof Error ? error.message : 'Failed to create schedule. Please try again.';
      showErrorToast('Error', msg);
    }
  };

  if (!isOpen) return null;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="right"
      width="sm"
      contentRef={scrollRef}
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Create Booking Ops Setting</h2>
          <p className="text-sm font-normal text-gray-500">New Booking Operational Setting.</p>
        </div>
      }
      footer={
        <Button
          variant="primary"
          onClick={handleSave}
          className="w-full py-3 bg-[#5e2aac] hover:bg-[#4a2287]"
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      }
    >
      <div className="flex-1">
        <div
          className="bg-gray-50 px-6 py-2 font-medium uppercase tracking-wider border-b border-gray-200 heading-field"
          style={{ fontSize: '12px' }}
        >
          Booking Operational Detail
        </div>

        <div className="p-6 pb-40">
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hub Location <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={hubOptions}
              value={formData.hubID}
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  hubID: typeof val === 'number' ? val : Number(val),
                }))
              }
              placeholder="Hub Location"
              className="w-full"
            />
          </div>

          <div className="mb-5">
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              Hari &amp; Tanggal Pengambilan Motor <span className="text-red-500 ml-1">*</span>
              <i className="ri-information-2-fill text-gray-400 ml-1"></i>
            </label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => {
                const isSelected = formData.activeDays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => handleDayToggle(day.value)}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${isSelected
                        ? 'bg-[#5e2aac] text-white border-[#5e2aac]'
                        : 'bg-white text-[#5e2aac] border-[#5e2aac] hover:bg-gray-50'
                      }`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Jam Pengambilan Motor (WIB) <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3 mb-3">
              {formData.timeWindows.map((tw, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="flex-1">
                    <SearchableDropdown
                      options={TIME_OPTIONS}
                      value={tw.startTime}
                      onChange={(val) => handleTimeWindowChange(index, 'startTime', String(val))}
                      placeholder="Start Time"
                      allowClear={false}
                      className="w-full"
                    />
                  </div>
                  {tw.startTime && (
                    <div className="flex-1">
                      <SearchableDropdown
                        options={TIME_OPTIONS}
                        value={tw.endTime}
                        onChange={(val) => handleTimeWindowChange(index, 'endTime', String(val))}
                        placeholder="End Time"
                        allowClear={false}
                        className="w-full"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveTimeWindow(index)}
                    disabled={formData.timeWindows.length === 1}
                    className="flex-shrink-0 h-10 w-10 flex items-center justify-center border border-gray-300 rounded-xl text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
            {formData.timeWindows.length < 3 && (
              <button
                type="button"
                onClick={handleAddTimeWindow}
                className="w-full py-2.5 border border-gray-200 rounded-md text-sm text-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-add-line"></i> Add Jam Pengambilan Motor
              </button>
            )}
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.isActive ? 'bg-[#5e2aac]' : 'bg-gray-200'
                  }`}
                onClick={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.isActive ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
              </button>
              <span className="text-[13px] text-gray-700">
                {formData.isActive ? 'Active' : 'Not Active'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </SideBarModal>
  );
};
